import { useEffect, useRef, useState } from "react";
import type { TimeclockConfig } from "@eagledispatch/shared";
import { apiFetch } from "../lib/api";
import { scanTagUid } from "../lib/nfc";
import { submitPunch, type SubmitPunchResult } from "../lib/punch";
import { useTenantPhone, noOpenShiftHelp, type TenantPhoneState } from "../lib/tenant-phone";
import Button from "./Button";
import Card from "./Card";

/** Clock Out ceremony modal — shared by DISPATCHER (App.tsx) and DRIVER (DriverApp). */
export interface DispatcherSignOutProps {
  onComplete: () => void;
  onCancel: () => void;
  /** Which surface mounted the ceremony. Default "DISPATCHER" = existing behavior. */
  role?: "DRIVER" | "DISPATCHER";
}

type PanelState =
  | "loading_config"
  | "scanning"
  | "submitting"
  | "error";

export default function DispatcherSignOut({
  onComplete,
  onCancel,
  role = "DISPATCHER",
}: DispatcherSignOutProps) {
  const [state, setState] = useState<PanelState>("loading_config");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [lastResultCode, setLastResultCode] = useState<string | null>(null);
  const phoneState: TenantPhoneState = useTenantPhone();
  const mountedRef = useRef(true);
  const nfcRequiredRef = useRef(true);
  const healAttemptedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    initFlow();
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const initFlow = async () => {
    setState("loading_config");
    setErrorMessage(null);
    setLastResultCode(null);

    let nfcRequired = true;
    try {
      // Fetch flag fresh directly (bypassing 30s cache)
      const config = await apiFetch<TimeclockConfig>("/api/v1/timeclock/config");
      nfcRequired = config.nfcRequired;
    } catch {
      // Fallback: treat as flag ON (fail-closed)
      nfcRequired = true;
    }

    if (!mountedRef.current) return;
    nfcRequiredRef.current = nfcRequired;

    if (nfcRequired) {
      startScanFlow();
    } else {
      submitNoTag();
    }
  };

  const handlePunchResult = (result: SubmitPunchResult, isScanMode: boolean) => {
    if (!mountedRef.current) return;

    if (result.ok === true) {
      onComplete();
      return;
    }

    // Self-heal: Server says NFC disabled, but we scanned
    if (result.code === "nfc_disabled" && isScanMode) {
      nfcRequiredRef.current = false;
      setLastResultCode(null);
      submitNoTag();
      return;
    }

    // Self-heal: Server says NFC required, but we submitted NO_TAG
    if (result.code === "nfc_required" && !isScanMode && !healAttemptedRef.current) {
      healAttemptedRef.current = true;
      nfcRequiredRef.current = true;
      setLastResultCode(null);
      startScanFlow();
      return;
    }

    setState("error");
    setLastResultCode(result.code ?? null);
    if (result.kind === "invalid_tag") {
      setErrorMessage("Tag not recognized. Clock out not recorded.");
    } else {
      setErrorMessage(result.message || "Clock out punch failed.");
    }
  };

  const submitNoTag = async () => {
    setState("submitting");
    setErrorMessage(null);
    // Defensive symmetry with startScanFlow: every retry clears the code
    // (not required for correctness — no fail-before-submit path — but it
    // keeps the invariant literal and future-proof; audit round 3 LOW).
    setLastResultCode(null);

    const res = await submitPunch("OUT");
    handlePunchResult(res, false);
  };

  const startScanFlow = async () => {
    setState("scanning");
    setErrorMessage(null);
    // Clear the punch-error code on every (re)entry: a user-driven Try Again
    // after a no_open_shift 409 must not bleed that code into a later
    // scan-failure error render (audit BUG #1 — stale-state-across-retry).
    setLastResultCode(null);

    const result = await scanTagUid(30_000);
    if (!mountedRef.current) return;

    if ("error" in result) {
      if (result.error === "cancelled") {
        onCancel();
        return;
      }
      setState("error");
      if (result.error === "unsupported") {
        setErrorMessage("NFC scanning is not supported on this device/browser.");
      } else if (result.error === "timeout") {
        setErrorMessage("Scan timed out. Please try again.");
      } else {
        setErrorMessage(result.error || "NFC scan failed.");
      }
      return;
    }

    setState("submitting");
    const res = await submitPunch("OUT", result.uid);
    handlePunchResult(res, true);
  };

  return (
    <div
      className="modal-overlay"
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(11, 30, 35, 0.65)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "16px",
      }}
    >
      <div style={{ maxWidth: "420px", width: "100%", textAlign: "center" }}>
        <Card className="login-card">
          <div style={{ padding: "16px 8px" }}>
            <div style={{ fontSize: "48px", marginBottom: "16px" }}>🏷️</div>

            {state === "loading_config" && (
              <>
                <h1 style={{ fontSize: "22px", marginBottom: "8px" }}>Clock Out…</h1>
                <p className="muted" style={{ fontSize: "14px" }}>
                  Checking timeclock settings…
                </p>
              </>
            )}

            {state === "scanning" && (
              <>
                <h1 style={{ fontSize: "22px", marginBottom: "8px" }}>Hold phone near tag</h1>
                <p className="muted" style={{ fontSize: "14px", marginBottom: "24px" }}>
                  Scan your registered NFC tag to clock out.
                </p>
                <Button variant="secondary" onClick={onCancel} style={{ width: "100%" }}>
                  Cancel
                </Button>
              </>
            )}

            {state === "submitting" && (
              <>
                <h1 style={{ fontSize: "22px", marginBottom: "8px" }}>Recording Clock Out…</h1>
                <p className="muted" style={{ fontSize: "14px" }}>
                  Submitting punch to dispatch server…
                </p>
              </>
            )}

            {state === "error" && (
              <>
                <h1 style={{ fontSize: "22px", marginBottom: "8px", color: "var(--red)" }}>
                  Clock Out Incomplete
                </h1>
                <div
                  className="banner banner-danger"
                  style={{ marginBottom: "20px", textAlign: "left" }}
                >
                  {errorMessage || "Clock out punch failed."}
                </div>
                {role === "DRIVER" && lastResultCode === "no_open_shift" && (
                  <p style={{ marginBottom: "16px" }}>{noOpenShiftHelp(phoneState)}</p>
                )}
                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  <Button
                    variant="primary"
                    onClick={nfcRequiredRef.current ? startScanFlow : submitNoTag}
                    style={{ width: "100%" }}
                  >
                    Try Again
                  </Button>
                  <Button variant="ghost" onClick={onCancel} style={{ width: "100%" }}>
                    Cancel
                  </Button>
                </div>
              </>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
